// The work item's key at the top of its screen, with "Copy link" beside it.
//
// As Jira draws it: hover the key and it underlines, and a link icon
// appears next to it; hover the icon for "Copy link"; click it and the
// address is on the clipboard — the icon turns into a green tick and the
// tip says "Copied!". The address is the work item's own page,
// https://<this site>/<KEY> (utils/workItemLink.js), which opens the work
// item full screen.
//
// The key itself keeps its link and its click (`href`, `onKeyClick`), so
// nothing that used to happen on a click of the key changes.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { workItemUrl } from '@modules/database/work-items/frontend/utils/workItemLink';
import { copyText } from '@modules/database/work-items/frontend/components/WorkItemKey/copyText';
import '@modules/database/work-items/frontend/components/WorkItemKey/WorkItemKey.css';

const RESET_MS = 2000;

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const CopiedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path d="m7.5 12.5 3 3 6-6.5" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The tip is portalled to <body> at viewport coordinates: the key sits a
// few pixels from the top of a panel that clips its overflow, so a tip
// drawn inside it would be cut off.
function Tip({ anchorRef, text }) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, top: r.top });
  }, [anchorRef, text]);
  if (!pos) return null;
  return createPortal(
    <span className="wik-tip" role="tooltip" style={{ left: pos.left, top: pos.top }}>{text}</span>,
    document.body,
  );
}

export default function WorkItemKey({ issueKey, href, onKeyClick, className = 'screen-panel-key' }) {
  const [state, setState] = useState('idle'); // 'idle' | 'copied' | 'failed'
  const [pointed, setPointed] = useState(false); // hovered or keyboard-focused
  const timer = useRef(null);
  const btnRef = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // the key often sits inside a clickable row
    const ok = await copyText(workItemUrl(issueKey, window.location.origin));
    setState(ok ? 'copied' : 'failed');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), RESET_MS);
  };

  const tip = state === 'copied' ? 'Copied!' : state === 'failed' ? "Couldn't copy" : 'Copy link';

  return (
    <span className={`wik wik--${state}`}>
      <a className={`${className} wik-key`} href={href} onClick={onKeyClick}>{issueKey}</a>
      <button
        ref={btnRef}
        type="button"
        className="wik-copy"
        onClick={copy}
        onMouseEnter={() => setPointed(true)}
        onMouseLeave={() => setPointed(false)}
        onFocus={(e) => {
          // Keyboard focus shows the tip; a mouse click's focus does not.
          let keyboard = false;
          try { keyboard = e.currentTarget.matches(':focus-visible'); } catch { keyboard = false; }
          if (keyboard) setPointed(true);
        }}
        onBlur={() => setPointed(false)}
        aria-label={state === 'copied' ? `Link to ${issueKey} copied` : `Copy link to ${issueKey}`}
      >
        {state === 'copied' ? <CopiedIcon /> : <LinkIcon />}
      </button>
      {(pointed || state !== 'idle') && <Tip anchorRef={btnRef} text={tip} />}
      <span className="wik-sr" aria-live="polite">{state === 'copied' ? 'Link copied to clipboard' : ''}</span>
    </span>
  );
}
